export type BulkAdjustmentType = 'percentage' | 'fixed';
export type BulkAdjustmentValues = { type: BulkAdjustmentType, value: number | undefined };
export const BULK_ADJUSTMENT_DEFAULT_VALUES: BulkAdjustmentValues = { type: 'percentage', value: undefined };

export const isBulkAdjustmentValueValid = (value: number | undefined): value is number =>
	value !== undefined && Number.isFinite(value) && value !== 0;

// Use the decimal representation, as BigDecimal.valueOf does on the server. Keeping the
// arithmetic exact until the final rounding avoids binary errors at whole-unit halves.
const decimalRatio = (value: number): [bigint, bigint] => {
	const [mantissa, exponent = '0'] = value.toString().split('e');
	const [whole, fraction = ''] = mantissa.split('.');
	const scale = fraction.length - Number(exponent);
	const coefficient = BigInt(whole + fraction);
	return scale >= 0 ? [coefficient, 10n ** BigInt(scale)] : [coefficient * 10n ** BigInt(-scale), 1n];
};

export const applyBulkAdjustment = (currentPrice: number, type: BulkAdjustmentType, value: number): number | undefined => {
	if (!Number.isFinite(currentPrice) || !Number.isFinite(value)) return undefined;
	const [base, baseScale] = decimalRatio(currentPrice);
	const [change, changeScale] = decimalRatio(value);
	const numerator = type === 'percentage'
		? base * (100n * changeScale + change)
		: base * changeScale + change * baseScale;
	const denominator = baseScale * changeScale * (type === 'percentage' ? 100n : 1n);
	if (numerator <= 0n) return 0;
	// Positive halves round up; negative results are clamped to zero.
	const rounded = Number((2n * numerator + denominator) / (2n * denominator));
	return Number.isFinite(rounded) ? rounded : undefined;
};
